import { Component, ElementRef, OnInit, QueryList, ViewChildren } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from '../_services/api.service';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { UserData } from '../_models/users';
import { NotificationService } from '../_services/notification.service';

@Component({
  selector: 'app-verify',
  templateUrl: './verify.component.html',
  styleUrl: './verify.component.css'
})
export class VerifyComponent implements OnInit {
  readonly length = 6;

  form: FormGroup;
  @ViewChildren('otpBox') otpBoxes!: QueryList<ElementRef>;
  token!: string;
  accessToken!: string;
  returnUrl = '/';

  error: boolean = false;
  errorMessage: string = "";
  loading: boolean = true;
  processing: boolean = false;
  buttonName: string = "Verify PIN"
  needsConfirm: boolean = false;

  pinStage: 'VERIFY' | 'NEW' | 'CONFIRM' = 'VERIFY';
  newPinFirst = '';
  pin = '';
  oldPin: string;

  constructor(
    private route: ActivatedRoute,
    private apiService: ApiService,
    private fb: FormBuilder,
    private notifyService: NotificationService,
    private router: Router
  ) {

    const controls: Record<string, FormControl<string>> = {};
    for (let i = 0; i < this.length; i++) {
      controls[`d${i}`] = new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.pattern(/^\d$/)]
      });
    }
    this.form = this.fb.group(controls);
  }

  ngOnInit(): void {
    this.accessToken = this.route.snapshot.queryParamMap.get('token') ?? '';
    this.returnUrl = this.route.snapshot.queryParamMap.get('returnUrl') ?? '/';
    this.checkToken();
  }

  checkToken() {
    this.error = false;
    this.loading = true;
    this.apiService.checkToken(this.accessToken).subscribe(
      data => {
        console.log('Token check data:', data);
        if (data.success === true) {
          this.loading = false;
          this.error = false;
          this.token = data.token.token;
        } else {
          this.error = true;
          this.errorMessage = data.error.additional_message || 'Invalid token';
        }
      }
    );
  }

  get otp(): string {
    return Array.from({ length: this.length }, (_, i) => this.form.get(`d${i}`)?.value ?? '').join('');
  }

  isComplete(): boolean {
    return this.otp.length === this.length && !this.otp.includes('');
  }

  focusIndex(i: number) {
    const el = this.otpBoxes.get(i)?.nativeElement;
    el?.focus();
    el?.select();
  }

  onInput(index: number, ev: Event) {
    const input = ev.target as HTMLInputElement;
    const raw = input.value ?? '';
    const lastChar = raw.slice(-1);

    if (!/^\d$/.test(lastChar)) {
      input.value = '';
      this.form.get(`d${index}`)?.setValue('');
      return;
    }

    // Keep only one digit
    input.value = lastChar;
    this.form.get(`d${index}`)?.setValue(lastChar);

    if (index < this.length - 1) this.focusIndex(index + 1);
  }

  onKeyDown(index: number, ev: KeyboardEvent) {
    const key = ev.key;
    const ctrl = this.form.get(`d${index}`);

    if (key === 'Backspace') {
      const currentVal = ctrl?.value ?? '';
      if (currentVal) {
        // clear current
        ctrl?.setValue('');
        // prevent browser doing weird selection
        ev.preventDefault();
        return;
      }

      // move back and clear previous
      if (index > 0) {
        this.form.get(`d${index - 1}`)?.setValue('');
        ev.preventDefault();
        this.focusIndex(index - 1);
      }
      return;
    }

    if (key === 'ArrowLeft') {
      ev.preventDefault();
      this.focusIndex(index - 1);
      return;
    }

    if (key === 'ArrowRight') {
      ev.preventDefault();
      this.focusIndex(index + 1);
      return;
    }
  }

  onPaste(ev: ClipboardEvent) {
    ev.preventDefault();

    const text = ev.clipboardData?.getData('text') ?? '';
    const digits = text.replace(/\D/g, '').slice(0, this.length);

    if (!digits) return;

    for (let i = 0; i < this.length; i++) {
      this.form.get(`d${i}`)?.setValue(digits[i] ?? '');
    }

    // Focus last filled or last box
    const last = Math.min(digits.length, this.length) - 1;
    this.focusIndex(Math.max(last, 0));
  }

  submit() {
    if (this.form.invalid) return;

    if (this.pinStage === 'VERIFY') {
      this.oldPin = this.otp;
      this.verifyPIN({ pin: this.otp });
      return;
    }

    if (this.pinStage === 'NEW') {
      // store first entry and ask to confirm
      this.newPinFirst = this.otp;
      this.form.reset();
      this.pinStage = 'CONFIRM';
      this.buttonName = "Confirm PIN";
      this.notifyService.showInfo("Please confirm your new 6-digit PIN.", "Info");
      return;
    }

    if (this.pinStage === 'CONFIRM') {
      const confirmPin = this.otp;

      if (confirmPin !== this.newPinFirst) {
        this.notifyService.showError("PINs do not match. Please try again.", "Error");
        this.form.reset();
        this.pinStage = 'NEW';
        this.buttonName = "Continue";
        return;
      }

      // ✅ Save new pin to server
      this.setNewPIN({ pin: confirmPin, old_pin: this.oldPin });
      return;
    }
  }

  verifyPIN( data ) {
    this.processing = true;
    this.buttonName = "Verifying...";

    this.apiService.verifyPin(data, this.token).subscribe(
      user => {
        this.processing = false
        if (user.success == true) {
          if (user.data.pinStatus.inactive) {
            this.notifyService.showInfo("Please enter a new 6-digit PIN to continue.", "Info");
            this.needsConfirm = true;

            this.pinStage = 'NEW';
            this.newPinFirst = '';
            this.form.reset();

            this.buttonName = "Continue";
            return;
          } else if (this.returnUrl && this.returnUrl !== '/') {
            this.router.navigateByUrl(this.returnUrl);
          } else {
            this.router.navigate(['/', this.accessToken]);
          }
          console.log('PIN verified successfully:', user);
        } else {
          localStorage.removeItem('access_token');
          this.notifyService.showError(user.error.message, "Error")
        }
        this.buttonName = "Verify PIN";
      }
    );
  }

  setNewPIN(data: { pin: string, old_pin: string }) {
    this.processing = true;
    this.buttonName = "Saving...";

    this.apiService.setPin(data, this.token).subscribe(
      res => {
        this.processing = false;

        if (res.success === true) {
          this.notifyService.showSuccess("PIN set successfully.", "Success");

          // reset flow back to normal
          this.needsConfirm = false;
          this.pinStage = 'VERIFY';
          this.newPinFirst = '';
          this.buttonName = "Verify PIN";

          localStorage.setItem('access_token', this.accessToken);

          // redirect
          if (this.returnUrl && this.returnUrl !== '/') {
            this.router.navigateByUrl(this.returnUrl);
          } else {
            this.router.navigate(['/', this.accessToken]);
          }
        } else {
          localStorage.removeItem('access_token');
          this.notifyService.showError(res.error?.message ?? "Failed to set PIN.", "Error");
          this.buttonName = "Confirm PIN";
        }
      },
      err => {
        localStorage.removeItem('access_token');
        this.processing = false;
        this.notifyService.showError("Network error. Please try again.", "Error");
        this.buttonName = "Confirm PIN";
      }
    );
  }
}
